import { useState } from "react";
import { Attachment, getVideoEmbedUrl } from "@/hooks/useAnnouncementAttachments";
import { FileText, ExternalLink, Download, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";

interface AttachmentDisplayProps {
  attachments: Attachment[];
}

export function AttachmentDisplay({ attachments }: AttachmentDisplayProps) {
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  if (!attachments || attachments.length === 0) return null;

  const images = attachments.filter((a) => a.attachment_type === "image");
  const videos = attachments.filter((a) => a.attachment_type === "video");
  const documents = attachments.filter((a) => a.attachment_type === "document");
  const links = attachments.filter((a) => a.attachment_type === "link");

  return (
    <div className="space-y-4 mt-4">
      {/* Photo Gallery */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {images.map((image) => (
            <button
              key={image.id}
              onClick={() => setLightboxImage(image.url)}
              className="aspect-square rounded-lg overflow-hidden bg-muted hover:opacity-90 transition-opacity"
            >
              <img
                src={image.url}
                alt={image.title || "Announcement image"}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      {/* Videos */}
      {videos.length > 0 && (
        <div className="space-y-3">
          {videos.map((video) => {
            const embedUrl = getVideoEmbedUrl(video.url);
            return embedUrl ? (
              <div key={video.id} className="relative aspect-video rounded-lg overflow-hidden bg-muted">
                <iframe
                  src={embedUrl}
                  title={video.title || "Video"}
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <a
                key={video.id}
                href={video.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <ExternalLink className="h-4 w-4" />
                {video.title || video.url}
              </a>
            );
          })}
        </div>
      )}

      {/* Documents */}
      {documents.length > 0 && (
        <div className="space-y-2">
          {documents.map((doc) => (
            <a
              key={doc.id}
              href={doc.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 bg-muted rounded-lg hover:bg-muted/80 transition-colors"
            >
              <FileText className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              <span className="flex-1 text-sm font-medium truncate">
                {doc.title || doc.file_name || "Document"}
              </span>
              <Download className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            </a>
          ))}
        </div>
      )}

      {/* Links */}
      {links.length > 0 && (
        <div className="space-y-2">
          {links.map((link) => (
            <a
              key={link.id}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
            >
              <ExternalLink className="h-5 w-5 text-primary flex-shrink-0" />
              <span className="flex-1 text-sm font-medium text-primary truncate">
                {link.title || link.url}
              </span>
            </a>
          ))}
        </div>
      )}

      {/* Lightbox for images */}
      <Dialog open={!!lightboxImage} onOpenChange={() => setLightboxImage(null)}>
        <DialogContent className="max-w-4xl p-0 bg-transparent border-none">
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-2 right-2 p-2 bg-background/80 rounded-full hover:bg-background z-50"
          >
            <X className="h-5 w-5" />
          </button>
          {lightboxImage && (
            <img
              src={lightboxImage}
              alt="Full size"
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
